import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router'

import { useUserContext } from 'context'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { Heading } from '../../../../components/ui/Heading'
import trackEvents from '../../../../constants/track-events'
import { query } from '../../../../graphql'
import { trackEvent } from '../../../../helpers/analytics'
import { useTalentsQuery } from '../../../../hooks/use-talents-query'
import { useTalentStorage } from '../../../pricing/hooks'
import { useDiscover } from '../../hooks/useDiscover'
import {
  createDefaultGoBackSequenceMessage,
  CULTURAL_FIT_DIMENSION_NAMES,
  MESSAGE_ROLES,
  MESSAGE_TYPES,
  RIASEC_FIT_DIMENSION_NAMES,
  sendSmartMessageSequence,
  startMatchingProfilePartThreeAssistantFlow
} from '../../utils'
import {
  filterLockedSuggestions,
  pickSpaceIdForOwner
} from '../../utils/talent-storage-lock'
import MandatoryBadge from './MandatoryBadge'

const LEVEL_COLORS = {
  high: 'bg-brightGreen',
  medium: 'bg-aivyOrange',
  low: 'bg-mediumRed'
}

export default function DimensionMatchBreakdown({
  matchingProfile,
  talentName,
  careerId,
  isLoading
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const language = i18n.language

  const { activeSpace } = useUserContext()
  const hasCompletedOnboarding = activeSpace?.hasCompletedOnboarding

  const {
    setAssistantIsTyping,
    sendMessageToAssistant,
    enableAssistant,
    hideAssistant,
    setAssistantDetail,
    assistantDetail,
    setCustomIsLoading,
    setIsChancesAndRisksLoading,
    shouldClickOnMandatoryDimension,
    setShouldClickOnMandatoryDimension,
    setShouldShowCompletionOnboardingModal,
    enableClickOnFirstDimension,
    enableClickOnFirstMiddleFitDimension,
    setEnableClickOnFirstMiddleFitDimension,
    setCompletionSummaryModalOpen,
    setShowFeedbackModal,
    setAssistantSpotlightSelector,
    clearAssistantMessages
  } = useDiscover()

  const {
    high_fit_label = '',
    medium_fit_label = '',
    low_fit_label = '',
    overall_insights = {},
    insights_title = '',
    chances_label = '',
    interpretation_label = '',
    risks_label = '',
    interviews_label = '',
    measures_label = '',
    personality_dimensions = [],
    skills_dimensions = [],
    cultural_fit = {},
    generalTranslations = {},
    system = {},
    culture_visible = false,
    riasec = {}
  } = matchingProfile || {}

  const { data } = useTalentsQuery()
  const talentStorage = useTalentStorage({ fetch: 'auto' })

  const interestsCategory = system?.categories?.data[3] || {}
  const cultureCategory = system?.categories?.data[4] || {}

  const is_culture_available = !!cultural_fit && culture_visible

  const userCultureScores = is_culture_available
    ? {
        HEROS_JOURNEY_score_coopcomp:
          cultural_fit.user_cultural_fit.HEROS_JOURNEY_score_coopcomp
            .user_dimension_value,
        HEROS_JOURNEY_score_orderchaos:
          cultural_fit.user_cultural_fit.HEROS_JOURNEY_score_orderchaos
            .user_dimension_value,
        // group_match === 100 || 75 || 50 || 25 || 0
        group_match: overall_insights.insights.culture.group_match
      }
    : null

  const { fits, insights } = overall_insights || {}
  const { culture, interests } = insights || {}

  const insights_labels = {
    insights_title,
    chances_label,
    interpretation_label,
    risks_label,
    interviews_label,
    measures_label
  }

  const dimensions = [...personality_dimensions, ...skills_dimensions]

  const hasClickedMandatoryDimension = useRef(false)
  const detailsTimeoutRef = useRef(null)
  const highFitListRef = useRef(null)

  // When user comes back from details, show Chancen & Risiken message after 1 second
  useEffect(() => {
    if (
      assistantDetail === false &&
      enableClickOnFirstMiddleFitDimension &&
      !hasCompletedOnboarding
    ) {
      // Clear any existing timeout
      if (detailsTimeoutRef.current) {
        clearTimeout(detailsTimeoutRef.current)
      }
      // Set a new timeout to show the message after 1 second
      detailsTimeoutRef.current = setTimeout(() => {
        startMatchingProfilePartThreeAssistantFlow({
          sendMessageToAssistant,
          setAssistantIsTyping,
          setCompletionSummaryModalOpen,
          setShowFeedbackModal,
          setAssistantSpotlightSelector,
          clearAssistantMessages,
          t,
          hideAssistant
        })
      }, 1000)
    }

    return () => {
      if (detailsTimeoutRef.current) {
        clearTimeout(detailsTimeoutRef.current)
      }
    }
    // Abhängigkeiten bewusst wie bisher, sonst laufen die Assistenten-Flows öfter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assistantDetail,
    enableClickOnFirstMiddleFitDimension,
    sendMessageToAssistant,
    setAssistantIsTyping,
    setCompletionSummaryModalOpen,
    setShowFeedbackModal,
    hasCompletedOnboarding
  ])

  useEffect(() => {
    if (shouldClickOnMandatoryDimension === false) return
    if (hasClickedMandatoryDimension.current) return
    // Trigger click on mandatory dimension
    const mandatoryDimension = dimensions.find(
      (d) => d.match.mandatory_criterion
    )
    if (mandatoryDimension) {
      onClickDimension({ data: { ...mandatoryDimension } })
      hasClickedMandatoryDimension.current = true
    }
    // Abhängigkeiten bewusst wie bisher, sonst laufen die Assistenten-Flows öfter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldClickOnMandatoryDimension, dimensions])

  const isMandatoryCriterion = (dimensionName) => {
    return dimensions.some(
      (d) =>
        (d.dimension.property.de === dimensionName ||
          d.dimension.property.en === dimensionName) &&
        !!d.match.mandatory_criterion
    )
  }

  const getDataByDimensionName = (dimensionName) =>
    dimensions.find(
      (d) =>
        d.dimension.property.de === dimensionName ||
        d.dimension.property.en === dimensionName
    )

  const createDimensionData = (fitArray) =>
    fitArray?.map((name) => ({
      name,
      isMandatoryCriterion: isMandatoryCriterion(name),
      data: getDataByDimensionName(name)
    })) || []

  const matchData = [
    {
      title: high_fit_label,
      level: 'high',
      dimensions: createDimensionData(fits?.high)
    },
    {
      title: medium_fit_label,
      level: 'medium',
      dimensions: createDimensionData(fits?.medium)
    },
    {
      title: low_fit_label,
      level: 'low',
      dimensions: createDimensionData(fits?.low)
    }
  ]

  const highFitDimensionCount = matchData?.[0]?.dimensions?.length || 0

  useEffect(() => {
    if (isLoading) return
    if (!highFitListRef.current) return

    const timeoutId = setTimeout(() => {
      const node = highFitListRef.current
      if (!node) return
      node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' })
    }, 2000)

    return () => clearTimeout(timeoutId)
  }, [isLoading, highFitDimensionCount])

  useEffect(() => {
    if (!enableClickOnFirstMiddleFitDimension) return
    if (hasCompletedOnboarding) return

    onClickDimension(
      {
        data: matchData[1].dimensions.find(
          (dim) => dim?.data?.match?.dimension_name === 'NUMERACY_score'
        ).data
      },
      'medium'
    )
    // Abhängigkeiten bewusst wie bisher, sonst laufen die Assistenten-Flows öfter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableClickOnFirstMiddleFitDimension, hasCompletedOnboarding])

  useEffect(() => {
    if (!enableClickOnFirstDimension) return

    onClickDimension(
      {
        data: matchData[0].dimensions.find(
          (dim) => dim?.data?.match?.dimension_name === 'PROBLEM_SOLVING_score'
        ).data
      },
      'high'
    )
    // Abhängigkeiten bewusst wie bisher, sonst laufen die Assistenten-Flows öfter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableClickOnFirstDimension])

  const getContainerColor = (level) => {
    switch (level) {
      case 'high':
        return LEVEL_COLORS.high
      case 'medium':
        return LEVEL_COLORS.medium
      case 'low':
        return LEVEL_COLORS.low
      default:
        return 'border-l-gray-400 bg-gray-50'
    }
  }

  // Derzeit nicht verdrahtet
  // eslint-disable-next-line no-unused-vars
  const onClickChancesAndRisks = (dimension) => {
    trackEvent(trackEvents.DISCOVER_PRESSED_NEXT_STEPS_PROMPT_PRIMARY_ACTION)
    setIsChancesAndRisksLoading(true)

    sendSmartMessageSequence({
      setIsTyping: setAssistantIsTyping,
      callbackFn: () => setIsChancesAndRisksLoading(false),
      messages: [
        {
          fn: () =>
            sendMessageToAssistant({
              id: 1,
              dimensionMatchMessageProps: {
                dimension: dimension.data.dimension,
                match: dimension.data.match,
                text_modules: dimension.data.text_modules,
                insights_labels,
                language,
                onlyDisplayChancesAndRisks: true,
                displayTextModules: true,
                displayInsights: false
              },
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.DIMENSION_CAREER_TALENT
            }),
          textLength: 150
        },
        createDefaultGoBackSequenceMessage({
          t,
          hideAssistant,
          sendMessageToAssistant,
          setAssistantIsTyping,
          shouldClickOnMandatoryDimension,
          setShouldClickOnMandatoryDimension,
          setShouldShowCompletionOnboardingModal,
          setShowFeedbackModal,
          setAssistantSpotlightSelector,
          clearAssistantMessages
        })
      ]
    })
  }

  const renderTalentsForCareer = (reachData, currentCareerId) => {
    const res = JSON.parse(reachData)
    const matchingTalents = []

    Object.entries(res).forEach(([talentId, careerData]) => {
      // Check if this talent has data for the current career
      if (careerData[currentCareerId]) {
        const careerArray = careerData[currentCareerId]

        // Get the first (and likely only) career match object
        const careerMatch = careerArray[0]

        if (careerMatch) {
          matchingTalents.push({
            id: talentId,
            name: careerMatch.person_name || t('common.unknown_talent'),
            matchScore:
              careerMatch.matching_score_to_career ||
              careerMatch.matching_score,
            talentId: careerMatch.user_id,
            category: 'default', // Adjust based on your needs
            careerData: careerMatch,
            onTalentClick: (talent) => {
              // Ohne Sperren der erste Space des Talents, sonst ein freier
              const spaceId = pickSpaceIdForOwner(
                data?.spaces,
                talent.talentId,
                talentStorage
              )

              trackEvent(
                trackEvents.DISCOVER_PRESSED_TALENT_MATCH_SUGGESTIONS_TALENT
              )
              hideAssistant()
              navigate(`/discover/match/${currentCareerId}/${spaceId}`)
            }
          })
        }
      }
    })
    return matchingTalents
  }

  // Derzeit nicht verdrahtet
  // eslint-disable-next-line no-unused-vars
  const onClickFindBetterMatch = async () => {
    trackEvent(trackEvents.DISCOVER_PRESSED_NEXT_STEPS_PROMPT_SECONDARY_ACTION)
    setCustomIsLoading(true)

    const reachData = await query({
      query: 'matchBatchIfYouCan',
      variables: {
        careers: [careerId],
        with_pre_matching: true
      }
    })

    // Gesperrte Talente schlägt Discover nicht vor
    const talents = filterLockedSuggestions(
      renderTalentsForCareer(reachData, careerId),
      { spaces: data?.spaces, storage: talentStorage }
    )

    setCustomIsLoading(false)

    sendSmartMessageSequence({
      setIsTyping: setAssistantIsTyping,
      messages: [
        {
          fn: () =>
            sendMessageToAssistant({
              id: 3,
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TALENT_MATCH_SUGGESTIONS,
              talentMatchSuggestionsProps: {
                title: t('discover.assistant.talent_match_suggestions.title'),
                description: t(
                  'discover.assistant.talent_match_suggestions.description'
                ),
                talents
              }
            }),
          textLength:
            t('discover.assistant.talent_match_suggestions.title').length +
            t('discover.assistant.talent_match_suggestions.description').length
        }
      ]
    })
  }

  const handleCulturalFitDimension = () => {
    sendSmartMessageSequence({
      setIsTyping: setAssistantIsTyping,
      messages: [
        {
          fn: () =>
            sendMessageToAssistant({
              id: 1,
              title: cultureCategory.name[language],
              description: generalTranslations.culture_description,
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TEXT
            }),
          textLength:
            generalTranslations.culture_description.length +
            cultureCategory.name[language].length
        },
        {
          fn: () =>
            sendMessageToAssistant({
              id: 2,
              description: `${culture.category_fit} ${culture.evaluation_level}`,
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TEXT
            }),
          textLength: `${culture.category_fit} ${culture.evaluation_level}`
            .length
        },
        {
          fn: () =>
            sendMessageToAssistant({
              id: 3,
              culturalFitDimensionMatchMessageProps: {
                companyCultureScores: cultural_fit.cultural_fit_scores,
                userCultureScores
              },
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.CULTURAL_FIT_CAREER_TALENT
            }),
          textLength: `${culture.category_fit} ${culture.evaluation_level}`
            .length
        },
        createDefaultGoBackSequenceMessage({
          t,
          hideAssistant,
          sendMessageToAssistant,
          setAssistantIsTyping,
          shouldClickOnMandatoryDimension,
          setShouldClickOnMandatoryDimension,
          setShouldShowCompletionOnboardingModal,
          setShowFeedbackModal,
          setAssistantSpotlightSelector,
          clearAssistantMessages
        })
      ]
    })
  }

  const handleRiasecFitDimension = () => {
    sendSmartMessageSequence({
      setIsTyping: setAssistantIsTyping,
      messages: [
        {
          fn: () =>
            sendMessageToAssistant({
              id: 1,
              title: interestsCategory.name[language],
              description: generalTranslations.interests_description,
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TEXT
            }),
          textLength:
            interestsCategory.name[language].length +
            generalTranslations.interests_description.length
        },
        {
          fn: () =>
            sendMessageToAssistant({
              id: 2,
              description: `${interests.category_fit} ${interests.evaluation_level}`,
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TEXT
            }),
          textLength: `${interests.category_fit} ${interests.evaluation_level}`
            .length
        },
        {
          fn: () =>
            sendMessageToAssistant({
              id: 3,
              riasecFitDimensionMatchMessageProps: {
                language,
                riasec
              },
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.RIASEC_FIT_CAREER_TALENT
            }),
          textLength: 500
        }
      ]
    })
  }

  const handleRegularDimension = async (dimension, level) => {
    const displayTextModules = level !== 'high'
    const messages = [
      {
        fn: () =>
          sendMessageToAssistant({
            id: 1,
            dimensionMatchMessageProps: {
              dimension: dimension.data.dimension,
              match: dimension.data.match,
              text_modules: dimension.data.text_modules,
              insights_labels,
              language,
              displayTextModules,
              displayInsights: true
            },
            role: MESSAGE_ROLES.ASSISTANT,
            type: MESSAGE_TYPES.DIMENSION_CAREER_TALENT
          }),
        textLength: 150
      }
    ]

    messages.push(
      createDefaultGoBackSequenceMessage({
        t,
        hideAssistant,
        sendMessageToAssistant,
        setAssistantIsTyping,
        shouldClickOnMandatoryDimension,
        setShouldClickOnMandatoryDimension,
        setShouldShowCompletionOnboardingModal,
        enableClickOnFirstDimension,
        enableClickOnFirstMiddleFitDimension,
        setEnableClickOnFirstMiddleFitDimension,
        setCompletionSummaryModalOpen,
        setShowFeedbackModal,
        setAssistantSpotlightSelector,
        clearAssistantMessages
      })
    )

    await sendSmartMessageSequence({
      setIsTyping: setAssistantIsTyping,
      messages
    })
  }

  const onClickDimension = async (dimension, level) => {
    trackEvent(trackEvents.DISCOVER_PRESSED_DIMENSION_MATCH_BREAKDOWN)

    enableAssistant()
    setAssistantDetail(true)

    if (CULTURAL_FIT_DIMENSION_NAMES.includes(dimension.name)) {
      handleCulturalFitDimension()
      return
    }

    if (RIASEC_FIT_DIMENSION_NAMES.includes(dimension.name)) {
      handleRiasecFitDimension()
      return
    }

    await handleRegularDimension(dimension, level)
  }

  if (isLoading) {
    return (
      <div className='flex flex-col gap-4 md:flex-row md:gap-x-4'>
        <div
          className={`min-h-64 flex-1 overflow-hidden rounded-lg border border-lightGrey`}
        >
          <div className='flex h-[3.25rem] w-full flex-row items-center bg-paleGrey'>
            <div className={clsx('h-full w-2')} />
            <Heading className={'ml-6'} level={4}>
              <Skeleton width={100} />
            </Heading>
          </div>
          <div className='flex max-h-72 flex-col gap-y-5 overflow-y-scroll px-6 py-7'>
            <Skeleton count={5} />
          </div>
        </div>
        <div
          className={`flex-1 overflow-hidden rounded-lg border border-lightGrey`}
        >
          <div className='flex h-[3.25rem] w-full flex-row items-center bg-paleGrey'>
            <div className={clsx('h-full w-2')} />
            <Heading className={'ml-6'} level={4}>
              <Skeleton width={100} />
            </Heading>
          </div>
          <div className='flex max-h-72 flex-col gap-y-5 overflow-y-scroll px-6 py-7'>
            <Skeleton count={5} />
          </div>
        </div>
        <div
          className={`flex-1 overflow-hidden rounded-lg border border-lightGrey`}
        >
          <div className='flex h-[3.25rem] w-full flex-row items-center bg-paleGrey'>
            <div className={clsx('h-full w-2')} />
            <Heading className={'ml-6'} level={4}>
              <Skeleton width={100} />
            </Heading>
          </div>
          <div className='flex max-h-72 flex-col gap-y-5 overflow-y-scroll px-6 py-7'>
            <Skeleton count={5} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      id='dimension-match-breakdown'
      className='flex flex-col gap-4 md:flex-row md:gap-x-4'
    >
      {matchData.map((item, index) => (
        <div
          key={item.title}
          className={`flex-1 overflow-hidden rounded-lg border border-lightGrey`}
        >
          <div className='flex h-[3.25rem] w-full flex-row items-center bg-paleGrey'>
            <div
              className={clsx('h-full w-2', getContainerColor(item.level))}
            />
            <Heading className={'ml-6'} level={4}>
              {item.title}
            </Heading>
          </div>
          <div
            className='flex max-h-72 flex-col gap-y-5 overflow-y-scroll px-6 py-7'
            ref={index === 0 ? highFitListRef : null}
          >
            {item.dimensions.length > 0 ? (
              item.dimensions.map((dimension, idx) => {
                const isHighProblemSolving =
                  item.level === 'high' &&
                  dimension?.data?.match?.dimension_name ===
                    'PROBLEM_SOLVING_score'

                const isMediumNumeracy =
                  item.level === 'medium' &&
                  dimension?.data?.match?.dimension_name === 'NUMERACY_score'

                return (
                  <div
                    key={`dimension-${index}-${idx}`}
                    className={clsx(
                      'flex flex-row items-center gap-x-2',
                      dimension.isMandatoryCriterion && 'mandatory-dimension'
                    )}
                    onClick={() => onClickDimension(dimension, item.level)}
                    role='button'
                    id={
                      isHighProblemSolving
                        ? 'dimension-high-fit'
                        : isMediumNumeracy
                          ? 'dimension-middle-fit'
                          : undefined
                    }
                  >
                    <div className='cursor-pointer text-sm font-medium text-buttonSecondaryBlue'>
                      {dimension.name}
                    </div>
                    {dimension.isMandatoryCriterion && <MandatoryBadge />}
                  </div>
                )
              })
            ) : (
              <div className='flex flex-col items-center justify-center py-8 text-center'>
                <div className='mb-1 text-2xl text-gray-400'>—</div>
                <p className='text-sm text-gray-500'>
                  {t('discover.evaluation.empty_category_dimensions')}
                </p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

DimensionMatchBreakdown.propTypes = {
  matchingProfile: PropTypes.object.isRequired,
  talentName: PropTypes.string.isRequired,
  careerId: PropTypes.string,
  isLoading: PropTypes.bool
}
